import PSTableDataSource from './PSTableDataSource.js';

/**
 * Data source for handling API table data in PS Table.
 *
 * PSTableDataSourceApiTable fetches data from database tables via the api/record API through grape.tables.select,
 * handling server-side sorting, pagination, and filtering. This is suitable for
 * large datasets that need to be processed on the server.
 *
 * @class PSTableDataSourceApiTable
 * @extends PSTableDataSource
 * @example
 * // Use with API table configuration
 * const dataSource = new PSTableDataSourceApiTable({
 *   data_source: {
 *     schema: 'public',
 *     table: 'users'
 *   },
 *   paging: pagingInstance,
 *   sorting: sortingInstance,
 *   filtering: filteringInstance
 * });
 */
class PSTableDataSourceApiTable extends PSTableDataSource {
	/**
	 * Creates a new PSTableDataSourceApiTable instance.
	 *
	 * @param {Object} options - Data source configuration options
	 * @param {Object} options.data_source - API table configuration
	 * @param {string} options.data_source.schema - Database schema name to query
	 * @param {string} options.data_source.table - Database table name to query
	 * @param {PSTablePaging} options.paging - The pagination instance for the ps-table component
	 * @param {PSTableSorting} options.sorting - The sorting instance for the ps-table component
	 * @param {PSTableFiltering} options.filtering - The filtering instance for the ps-table component
	 */
	constructor(options) {
		// TODO: Handle potential knockout observable properties.
		super(options);
		this.mode = 'API Table';
	}

	/**
	 * Retrieves data from the API table source.
	 *
	 * Builds a query object with sorting, pagination, and filtering parameters,
	 * then fetches data from the Grape API. Updates pagination information
	 * with the response metadata.
	 *
	 * @returns {Promise<Array<Object>>} A promise that resolves to the fetched data array
	 */
	async select() {
		let query = {
			schema: this.data_source.schema,
			table: this.data_source.table,
			fields: this.data_source.fields
		};

		// Set sorting variables.
		let sortfield = this.sorting.sortfield();
		let sortorder = this.sorting.sortorder();
		if (sortfield?.length > 0)
		{
			query.sortfield = sortfield;
			query.sortorder = sortorder;

			if (!query.sortfield) delete query.sortfield;
			if (!query.sortorder) delete query.sortorder;
		}

		// Set paging variables.
		let page_size = this.paging.page_size();
		let page_number = Math.max(1, this.paging.page_number());
		let offset = (page_number - 1) * page_size;
		query.offset = offset;
		query.limit = page_size;

		// Set filtering variables.
		let filters = this.filtering.effective_filters();
		if (filters && filters.filter.length > 0)
		{
			query.filter = filters.filter;
			query.filter_join = filters.filter_join;

			if (!query.filter_join) delete query.filter_join;
		}

		// Fetch data
		let res = await Grape.tables.select(query);
		let rows = res.records ?? [];

		if (isNaN(res.offset)) res.offset = 0;
		if (isNaN(res.total)) res.total = 0;

		// Update page information
		this.paging.offset(res.offset);
		this.paging.total_records(res.total);

		if (res.status == 'ERROR')
			throw new Error(res.message);

		// Return data
		return rows;
	}
}

export default PSTableDataSourceApiTable;
